import React from 'react';
import { connect } from 'react-redux';
import { Button } from 'reactstrap';
import { Dispatch } from 'redux';
import HttpService from '../../../../services/rest/HttpService';
import { toast } from 'react-toastify';
import Dropzone from 'dropzone';
import * as helpers from './helpers/image-upload-button.helper';
import './image-upload-button-container.scss';
import {
	returnObjectForImageUploadFailed,
	returnObjectForImageUploadRequest,
	returnObjectForImageUploadSuccess,
} from '../../../../store/action-creators/ImageActionCreator';
import WatermarkService from '../../../../services/watermark-service/WatermarkService';
import Watermark from '../../../../models/watermarks/Watermark';
import Image from '../../../../models/image/Image';
import { WATERMARK_ID, WATERMARK_POSITION, WATERMARK_PROJECT } from '../../../../services/watermark-service/watermark-constants';
import { checkForWatermarkFeature } from '../../../../services/watermark-service/watermark-helper';
import { checkIfWatermarkIsValid } from './helpers/imago-image-upload.helper';
import REACT_APP_URLS from '../../../../global-helpers/global-url.helpers';
import {
	Properties,
	State,
	DropzoneFile,
	DropzoneErrorResponse,
	ImageUploadResponse,
	ContentApiResponse,
	ImageWithFileName,
	MapDispatchToProps,
	MapStateToProps,
	ImageUploadAction,
} from './properties/image-upload-button-container.properties';
import { AppState } from '../../../../store/store';

class ImageUploadButtonContainer extends React.Component<Properties, State> {
	private dropzone: Dropzone = {} as Dropzone;
	private watermarkService: WatermarkService = {} as WatermarkService;
	watermarkEnabled = checkForWatermarkFeature();
	private processedErrors: Set<string> = new Set();

	constructor(props: Properties) {
		super(props);
		Dropzone.autoDiscover = false;
		this.state = {
			isFileUploading: false,
			displayError: false,
			errorText: '',
			defaultWatermark: Watermark.builder().build(),
			originalFileNames: {},
		};
	}

	checkWatermarkEnabled() {
		if (this.props.applyQuickWatermark) {
			this.initWaterMarkService() &&
				this.watermarkService.getDefaultWatermark().then((defaultWatermark) => {
					this.setState({ defaultWatermark: defaultWatermark });
				});
		}
	}

	componentDidMount(): void {
		if (this.props.display) {
			this.initDropzone();
		}
		this.checkWatermarkEnabled();
	}

	componentDidUpdate(prevProps: Readonly<Properties>): void {
		if (prevProps.applyQuickWatermark !== this.props.applyQuickWatermark) {
			this.checkWatermarkEnabled();
		}
	}

	initWaterMarkService() {
		if (checkForWatermarkFeature()) {
			this.watermarkService = new WatermarkService();
			return true;
		} else return false;
	}

	getFileNameWithoutExtension(fileName: string): string {
		return fileName.replace(/\.[^/.]+$/, '');
	}

	initDropzone() {
		const { project, imageUploadButtonId, assetsProject } = this.props;
		const imageApiBaseUrl = this.props.imageApiURL ? this.props.imageApiURL : REACT_APP_URLS.REACT_APP_IMAGE_API_BASE_URL;
		const projectHeader = assetsProject ? 'assets' : project.domain;

		this.dropzone = new Dropzone(`#${imageUploadButtonId}`, {
			url: imageApiBaseUrl + '/upload',
			method: 'post',
			maxFilesize: 15,
			maxThumbnailFilesize: 15,
			createImageThumbnails: true,
			timeout: 300000,
			parallelUploads: 10,
			headers: { Project: projectHeader, 'X-Project': projectHeader },
			acceptedFiles: 'image/*',
			accept: (file: DropzoneFile, done: (error?: string | Error) => void) => {
				file.accept = () => done();
				file.reject = () => {
					done(helpers.FILE_DIMENS_TOO_BIG);
					this.props.imageUploadFailed();
				};
				file.rejectTimeout = () => {
					done(helpers.FILE_UPLOAD_TIMEOUT);
					this.props.imageUploadFailed();
				};
			},
			dictFileTooBig: 'File is too big',
		});

		this.initDropzoneListeners();
	}

	//TODO: REFACTOR
	initDropzoneListeners() {
		let imagePaths: string[] = [];
		let fileNames: { [path: string]: string } = {};
		const dropZoneOnAddFile = 'addedfile';
		const dropZoneOnError = 'error';
		const dropZoneOnSuccess = 'success';
		const dropZoneOnQueueComplete = 'queuecomplete';
		const dropZoneOnSending = 'sending';
		const dropZoneOnThumbnail = 'thumbnail';

		this.dropzone.on(dropZoneOnAddFile, (file: DropzoneFile) => {
			const fileName = this.getFileNameWithoutExtension(file.name);
			fileNames[file.name] = fileName;
			const fileKey = `${file.name}_${file.size}`;
			this.processedErrors.delete(fileKey);

			this.toggleUploadingState(true);
			this.toggleErrorState('', false);
			this.props.imageUploadRequest();
		});

		this.dropzone.on(dropZoneOnError, (file: DropzoneFile, errorMessage: string | DropzoneErrorResponse) => {
			const fileKey = `${file.name}_${file.size}_${errorMessage}`;

			if (!this.processedErrors.has(fileKey)) {
				this.processedErrors.add(fileKey);
				this.toggleUploadingState(false);
				this.toggleImageUploadErrorToast(errorMessage);
				this.props.imageUploadFailed();
			}
		});

		this.dropzone.on(dropZoneOnSuccess, (file: DropzoneFile, response: ImageUploadResponse) => {
			const imagePath = response && response.path ? response.path : '';
			imagePaths.push(imagePath);

			if (imagePath && fileNames[file.name]) {
				this.setState((prevState) => ({
					originalFileNames: {
						...prevState.originalFileNames,
						[imagePath]: fileNames[file.name],
					},
				}));
			}
		});

		this.dropzone.on(dropZoneOnQueueComplete, () => {
			const fileNamesForPaths = { ...this.state.originalFileNames };
			this.props.assetsProject
				? this.props.onImageUploadSuccess(imagePaths[0], true, undefined, fileNamesForPaths[imagePaths[0]])
				: this.uploadToContentApi(imagePaths, fileNamesForPaths);

			imagePaths = [];
			fileNames = {};
			this.processedErrors.clear();
			this.toggleUploadingState(false);
		});

		this.dropzone.on(dropZoneOnSending, (file: DropzoneFile, xhr: XMLHttpRequest, formData: FormData) => {
			this.props.applyQuickWatermark && this.state.defaultWatermark.id && formData.append(WATERMARK_ID, this.state.defaultWatermark.id);
			this.props.applyQuickWatermark &&
				this.state.defaultWatermark.project &&
				formData.append(WATERMARK_PROJECT, this.state.defaultWatermark.project);
			this.props.applyQuickWatermark &&
				this.state.defaultWatermark.position &&
				formData.append(WATERMARK_POSITION, this.state.defaultWatermark.position);
		});

		this.dropzone.on(dropZoneOnThumbnail, (file: DropzoneFile) => {
			if (file.width && file.height && file.width * file.height > helpers.MAX_DIMENS_SIZE) {
				const fileKey = `${file.name}_${file.size}_${helpers.FILE_DIMENS_TOO_BIG}`;
				if (!this.processedErrors.has(fileKey)) {
					this.processedErrors.add(fileKey);
					this.dropzone.removeFile(file as any);
					helpers.handleErrors(helpers.FILE_DIMENS_TOO_BIG, this.props.t);
					this.toggleUploadingState(false);
					this.props.imageUploadFailed();
				}
			} else {
				file.accept && file.accept();
			}
		});
	}

	uploadToContentApi(imagePaths: string[], fileNames: { [path: string]: string }) {
		if (imagePaths.length > 0) {
			const headers = { Project: this.props.project.domain };

			const requests = imagePaths.map((imagePath) => {
				const originalFileName = fileNames[imagePath];
				return helpers.constructRequestsFromFilePaths(
					[imagePath],
					headers,
					this.props.applyQuickWatermark && checkIfWatermarkIsValid(this.state.defaultWatermark),
					originalFileName,
				)[0];
			});

			HttpService.all(requests)
				.then((response: ContentApiResponse[]) => {
					this.toggleUploadingState(false);

					const lastResponseData = response[response.length - 1].data.data;
					const imagePath = imagePaths[imagePaths.length - 1];
					const originalFileName = fileNames[imagePath];

					this.props.onImageUploadSuccess(lastResponseData.id, false, lastResponseData as Image, originalFileName);

					this.onImageListReceived(response, fileNames, imagePaths);
					this.props.imageUploadSuccess();
					toast.success(this.props.t('image_upload_success'));
				})
				.catch((error: Error) => {
					this.toggleUploadingState(false);
					this.props.toggleUploading && this.props.toggleUploading(false);
					this.props.imageUploadFailed();
					helpers.handleErrors(helpers.FILE_UPLOAD_FAILED_CONTENT_API, this.props.t);
				});
		}
	}

	onImageListReceived(responseList: ContentApiResponse[], fileNames: { [path: string]: string }, imagePaths: string[]) {
		if (this.props.onMultiImageUploadSuccess) {
			const imageList = helpers.imageListMultipleResponseToImageList(responseList);

			const imageListWithFileNames = imageList.map((image: Image, index: number) => {
				const imagePath = imagePaths[index];
				if (imagePath && fileNames[imagePath]) {
					return {
						...image,
						originalFileName: fileNames[imagePath],
					} as ImageWithFileName;
				}
				return image;
			});

			this.props.onMultiImageUploadSuccess(imageListWithFileNames);
		}
	}

	toggleUploadingState(isUploading: boolean) {
		this.props.toggleUploading && this.props.toggleUploading(isUploading);
		this.setState({
			isFileUploading: isUploading,
		});
	}

	toggleErrorState(error: string, display: boolean) {
		this.setState({
			...this.state,
			errorText: error,
			displayError: display,
		});
	}

	toggleImageUploadErrorToast(error: string | DropzoneErrorResponse) {
		if (typeof error !== 'string' && error.message) {
			helpers.handleErrors(error.message, this.props.t);
		} else if (typeof error === 'string') {
			helpers.handleErrors(error, this.props.t);
		}
	}

	render() {
		const { t, blockStyle, imageUploadButtonId, display, size, label } = this.props;
		const { isFileUploading, displayError, errorText } = this.state;

		if (!display) {
			return null;
		}

		return (
			<div className={'scoped-dropzone-style'}>
				{label && label === 'browse' ? (
					<Button id={imageUploadButtonId} color='info' size={size ? size : ''} className={'text-white browse-btn'}>
						{t('browse')}
					</Button>
				) : (
					<Button id={imageUploadButtonId} color='info' size={size ? size : ''} className={blockStyle ? 'btn-block text-white' : `text-white`}>
						<i className={helpers.appendUploadingClass(isFileUploading)} /> {t('upload_image')}
					</Button>
				)}
				{displayError && (
					<div className={'alert alert-danger'} role='alert'>
						{errorText}
					</div>
				)}
			</div>
		);
	}
}

function mapDispatchToProps(dispatch: Dispatch<ImageUploadAction>): MapDispatchToProps {
	return {
		imageUploadRequest: () => dispatch(returnObjectForImageUploadRequest()),
		imageUploadSuccess: () => dispatch(returnObjectForImageUploadSuccess()),
		imageUploadFailed: () => dispatch(returnObjectForImageUploadFailed()),
	};
}

function mapStateToProps(state: AppState): MapStateToProps {
	return {
		imageApiURL: state.project.currentProject.imageApiUrl,
	};
}

export default connect(mapStateToProps, mapDispatchToProps)(ImageUploadButtonContainer);
